"use strict";

import AppUtil from "/js/util/app-util.js";
import FadeUtil from "/js/util/fade-util.js";
import Constants from "/js/core/constants.js";
import Util from "/js/util/util.js";
import FocusUtil from "/js/util/focus-util.js";
import Metrics from "/js/util/metrics.js";
import Global from "/js/core/global.js";
import Analytics from "/js/util/analytics.js";
import Strings from "/js/util/strings.js";
import PrimerMaster from "/js/util/primermaster.js";
import ServiceUtil from "/js/util/service-util.js";
import DragDelegate from "/js/util/drag-delegate.js";
import UserLessons from "/js/core/user-lessons.js";
import MinicourseView from "/js/top-level-views/minicourse-view.js";
import LessonView from "/js/top-level-views/lesson-view.js";
import DoneView from "/js/top-level-views/done-view.js";
import RecapView from "/js/top-level-views/recap-view.js";
import AccountView from "/js/top-level-views/account-view.js";
import SignView from "/js/top-level-views/sign-in-view";
import AppTransitionUtil from "/js/util/app-transition-util";
import StringsPagePopulator from '/js/util/strings-page-populator';

/**
 * The "application" class. Is the app entrypoint. Gets exposed to page via webpack setting.
 *
 * Does two main things:
 * (1) Bootstraps the app (handles passed-in params, loading sequence, etc.).
 * (2) "Owns", manages, and coordinates transitions between the top level views:
 * minicourse, lesson, done, and recap (plus others). Plus including sign-in-related concerns.
 *
 * NB: Any 'class members' that are made public are done so for use by AppTransitionUtil.
 */
export default class P4wApp {

  // Top-level views
  minicourseView;
  lessonView;
  doneView;
  recapView;
  #accountView;
  #signInView;
  #$cta = $("#mainCta"); // TODO: Wrap this in a class
  #$footer = $("#footer");
  #$footerBackButton = this.#$footer.find("#footerBackButton");
  #$footerPrivacyLink = this.#$footer.find("#footerPrivacyLink");
  #$footerLogo = this.#$footer.find("#footerLogo");
  #$closeButton = $('#close');

  #transitionUtil;

  /**
   * Describes current user sign-in state:
   * "no_gapi" - Google API is not yet initialized
   * "signed_out" - User is not signed in
   * "sign_in_flow" - App is in the middle of the signing-in-and-downloading-data flow
   * "okay" - User is signed in and has their data
   */
  #signInStatus = "no_gapi";

  #isMobileWidth;
  #wasMobileWidth;
  #didLayoutChange;

  /**
   * Bootstrap sequence:
   * constructor(), loadConfig(), loadStrings(), loadPrimerMaster(), initViews(), animateIn()
   */
  constructor() {
    const queryParams = document.location.href;

    this.#transitionUtil = new AppTransitionUtil(this);

    // Add reference to this as `window.Primer.app` as a dev/debugging convenience:
    if (!window.Primer) {
      window.Primer = {};
    }
    window.Primer.app = this;

    Global.parentOrigin = Util.getParameterByName("origin", queryParams);

    const hasDevSubstring = (s) => {
      return s && (s.includes("localhost") || s.includes("127.0.0.1") || s.includes("test.com"));
    };

    Global.isProd =
        (window.location.hostname === "webapp.yourprimer.com")
        || (window.location.hostname === "webapp-dot-gweb-learn10x.appspot.com");
    Global.isDev =
        Util.getParameterByName("dev", queryParams) // queryparam "dev" to force on
        || hasDevSubstring (window.location.hostname);

    if (hasDevSubstring(Global.parentOrigin)) {
      cl('special case for when hostpage is on the test server');
      Global.isProd = false;
      Global.isDev = true;
    }

    // Queryparam "erase" (dev convenience)
    if (Util.getParameterByName("erase", queryParams)) {
      UserLessons.erase();
      alert("Erased local UserLessons.");
      window.location.href = Util.removeParameterFromUrl(window.location.href,
          "erase");
      return;
    }

    // Queryparam "live" (puts app into "live mode")
    if (Util.getParameterByName("live", queryParams)) {
      Global.isLiveMode = true;
    }

    // Queryparam "lessonid"
    let lessonId = Util.getParameterByName("lessonid", queryParams);
    if (lessonId) {
      if (lessonId.indexOf("lesson-") === -1) {
        lessonId = "lesson-" + lessonId;
      }
      Global.lessonId = lessonId;
    }

    // Queryparam "display"
    let s = Util.getParameterByName('display', queryParams);
    if (s === "overlay" || s === "standalone" || s === "inline") {
      Global.displayMode = s;
    } else {
      clw("WARNING: no value supplied for `display`; will set to `standalone`");
      s = "standalone"
    }
    Global.displayMode = s;

    // Show close button immediately (when applicable).
    FocusUtil.doStandardTreatment(this.#$closeButton);
    this.#updateCloseButton();

    UserLessons.init(false);
    this.#addListeners();
    this.#loadConfig();

    if (Global.isDev && false) {
      FocusUtil.printActiveElementChanges();
    }
  }

  /**
   * Add listeners for the custom events (where events bubble up from subviews), plus.
   */
  #addListeners() {
    $(window).on("is-touch-device-determined", e => this.#onTouchDeviceDetermined(e));
    $(window).on('minicourse-item-select', (...rest) => this.#onMinicourseItemSelect(...rest));
    $(window).on("minicourse-sidebar-open", e => this.#openMinicourseSidebar());
    $(window).on("minicourse-sidebar-close", e => this.#closeMinicourseSidebar());
    $(window).on('next-lesson-button', (...rest) => this.#onNextLessonButton(...rest));
    $(window).on('lesson-done', e => this.#onLessonDone(e));
    $(window).on("done-view-recap-button", e => this.#onDoneViewRecapButton(e));
    $(window).on('done-view-back-to-dashboard-button', e => this.#onDoneViewBackToDashboard(e));
    $(window).on("recap-view-replay", e => this.#onRecapViewReplay(e));
    $(window).on("gallery-thumb", (...rest) => this.#onGalleryThumbClick(...rest));
    $(window).on("gallery-close", e => this.#closeLessonGallery());
    $(window).resize(e => this.#onWindowResize(e));

    this.#$footerBackButton.on("click tap", e => this.#onFooterBackButtonClick(e));
    FocusUtil.doStandardTreatment(this.#$footerBackButton);

    this.#$footerLogo.on("click tap", e => this.#onFooterLogoClick(e));
    $("#minicourseMobileDownloadCta").on("click tap", e => this.#onMobileDownloadCtaClick(e));
    $("#recapMobileDownloadCta").on("click tap", e => this.#onRecapMobileDownloadCtaClick(e));
    $('body').on('click tap', e => this.#onBodyClick(e));

    this.#$closeButton.on("click tap", e => this.#onCloseButton(e));

    $(document).on("keydown", e => this.#onDocumentKeyDown(e));

    if (Global.isLiveMode) {
      $(document).on("gapi-initialized", (...rest) => this.#onGapiInit(...rest));
      $(document).on("sign-in-button", e => this.#onSignInButton(e));
      $('#signOutButton').on("click tap", o => this.#onSignOutButton(o));
    }
  }

  #loadConfig() {

    const configBaseFilename = Util.getParameterByName("config");
    cl('config:', configBaseFilename);

    if (!configBaseFilename) {
      // Queryparam "all" (dev convenience, adds all existing lessons to the minicourse view)
      const shouldMakeMaximumMinicourse = Util.getParameterByName("all") || Global.isLiveMode;
      if (shouldMakeMaximumMinicourse) {
        const dummyData = {
          minicourse: {
            id: "minicourse-full-list",
                title: "All Lessons",
            copy: "",
            credit: "",
            lessonIds: []
          }
        };
        this.#onConfigLoaded(dummyData);
        return;
      }

      this.#loadStrings();
      return;
    }

    // Load config file
    const onError = function (jqxhr, textStatus, error) {
      Analytics.trackStartupError("configJson", jqxhr.status);
    };
    const baseFilename = configBaseFilename.replace(".json", "");
    const url = `${Constants.configFileBasePath}${baseFilename}.json`;
    $.ajax({
      url: url,
      dataType: "json",
      success: this.#onConfigLoaded,
      error: onError,
      context: this
    });
  }

  #onConfigLoaded(data) {
    cl('config json loaded:', data);

    // Language code (supercedes any queryparam)
    Global.languageCode = data.language ? data.language.toLowerCase() : "en";

    Global.partnerId = data.partnerId;

    // Minicourse data
    if (data.minicourse) {
      Global.minicourseData = data.minicourse;
      // Re-init UserLessons w/ local storage enabled meh
      UserLessons.init(true);
    }

    // CTA data
    if (data.cta && data.cta.label && data.cta.url) {
      Global.setCta(data.cta.label, data.cta.url);
    }

    this.#loadStrings();
  }

  #loadStrings() {

    // First, init analytics
    if (Global.minicourseData) {
      Analytics.init(Global.minicourseData.id, "minicourse", Global.partnerId, Global.languageCode, Metrics.isMobileWidth);
    } else {
      Analytics.init(Global.lessonId, "lesson", Global.partnerId, Global.languageCode, Metrics.isMobileWidth);
    }

    // Queryparam "devlang" (overrides any value from config file)
    let languageCode = Util.getParameterByName("devlang");
    if (languageCode) {
      Global.languageCode = languageCode;
    }

    Strings.load(Global.languageCode, (result) => this.#onStringsLoaded(result));
  }

  #onStringsLoaded(result) {
    if (!result) {
      return;
    }
    StringsPagePopulator.populatePageStaticElements();
    // And also populate privacy policy links:
    $("#footerPrivacyLink").attr("href", Strings.get("privacy_policy_url"));
    $("#mobilePrivacy").attr("href", Strings.get("privacy_policy_url"));

    this.#loadPrimerMaster();
  }

  #loadPrimerMaster() {
    PrimerMaster.load(
        Global.languageCode,
        () => this.#onPrimerMasterLoaded(),
        () => {});
  }

  #onPrimerMasterLoaded() {
    // Queryparam "all" (dev convenience, adds all existing lessons to the minicourse view)
    const shouldMakeMaximumMinicourse = Util.getParameterByName("all") || Global.isLiveMode;
    if (shouldMakeMaximumMinicourse) {
      Global.minicourseData.lessonIds = Object.keys(PrimerMaster.lessonMap);
    }
    // Queryparam "complete" (dev convenience, sets all but one lessons to complete)
    if (Util.getParameterByName("complete") && Global.minicourseData) {
      UserLessons.clear();
      UserLessons.setAllLessonsInMinicourseToComplete(true);
      alert("All lessons in minicourse set to complete (except one)");
      window.location.href = Util.removeParameterFromUrl(
          window.location.href, "complete");
    }

    this.#initViews();
  }

  #initViews() {
    // Init some of the top-level "view controllers".
    this.lessonView = new LessonView();
    this.doneView = new DoneView();
    this.recapView = new RecapView();
    if (Global.minicourseData) {
      this.minicourseView = new MinicourseView(Global.minicourseData);
    }
    if (Global.isLiveMode) {
      this.#accountView = new AccountView();
      this.#signInView = new SignView();
      this.#updateLiveModeViews();
    }

    // Init global CTA
    if (Global.hasCta) {
      $("body").addClass("hasCta");
      this.#$cta.show();
      this.#$cta.text(Global.ctaLabel);
      this.#$cta.attr('href', Global.ctaLink);
      this.#$cta.on("click tap", e => this.#onMainCtaClick(e));
    }
    // Footer back button
    if (Global.minicourseData) {
      this.#$footerBackButton.css("display", "block");
    }
    // Init yourprimer.com urls
    const url = Global.fullYourPrimerUrl;
    let $a = this.#$footerLogo.find("a");
    $a.attr("href", url);
    $a = $("#minicourseMobileDownloadCta a");
    $a.attr("href", url);
    $a = $("#recapMobileDownloadCta a");
    $a.attr("href", url);

    // Init <head> properties to make page look more presentable when it is not
    // wrapped by an iframe.
    document.title = (Global.minicourseData && Global.minicourseData.title)
        ? Global.minicourseData.title
        : "Primer";
    if (Global.minicourseData && Global.minicourseData.copy) {
      let metaDesc = `<meta name="description" content="${Global.minicourseData.copy}" />`;
      $("head").append(metaDesc);
    }

    Global.printValues();

    if (Global.isLiveMode && this.#signInStatus !== "okay") {
      // TODO: Is something missing here?
      return;
    }
    this.#animateIn();
  }

  #updateLiveModeViews() {
    if (!this.#signInView) {
      return;
    }
    switch (this.#signInStatus) {
      case "no_gapi":
        this.#signInView.show(false);
        this.#accountView.hide();
        break;
      case "signed_out":
        this.#signInView.show(true);
        this.#signInView.buttonEnabled = true;
        this.#accountView.hide();
        break;
      case "sign_in_flow":
        this.#signInView.buttonEnabled = false;
        this.#accountView.hide();
        break;
      case "okay":
        this.#signInView.hide();
        this.#accountView.show();
        break;
      default:
        clw("logic error: " + this.#signInStatus);
        break;
    }
  }

  /**
   * Animates in the first main view (ie, minicourse or loose lesson).
   * This is the end of the startup flow.
   */
  #animateIn() {
    if (Global.minicourseData) {

      this.#transitionUtil.startupToMinicourse();
      Analytics.trackSetView();

      // Queryparam "miniauto" (dev convenience, selects a lesson by index)
      const i = parseInt(Util.getParameterByName("miniauto"));
      if (!isNaN(i)) {
        setTimeout(() => {
          const lessonId = Global.minicourseData.lessonIds[i];
          this.#doMinicourseFullItemSelect(lessonId);
        }, 500);
      }

    } else if (Global.lessonId) {

      this.#transitionUtil.startupToLesson(Global.lessonId);

    } else {

      Analytics.trackStartupError("missingRequiredParam");
    }

    this.#resize();
  }

  // -----------------------------------------------------------------------------------------------
  // Various

  /**
   * Any and all programmatic resizing logic should get triggered through here.
   * Programmatic sizing and positioning should be avoided as much as possible, ofc.
   */
  #resize() {
    Global.cardScale = Metrics.calcCardScale();

    if (this.lessonView) {
      this.lessonView.resize();
    }
    if (this.minicourseView) {
      this.minicourseView.resize();
    }
    if (this.doneView) {
      this.doneView.resize();
    }
    if (this.recapView) {
      this.recapView.resize();
    }
    this.#updateCloseButton();
  }

  loadLessonAndCallback(callback) {
    const onError = function (jqxhr, textStatus, error) {
      Analytics.trackStartupError("lessonJson", jqxhr.status);
    };
    const onSuccess = function (json) {
      Global.setAndMassageLessonData(json);
      if (callback) {
        callback();
      }
    };
    const path = Constants.getLessonJsonPath(Global.lessonId, Global.languageCode);
    $.ajax({
      url: path,
      dataType: "json",
      success: onSuccess,
      error: onError,
      context: this
    });
  }

  /** User has selected an item from the full-size minicourse view. */
  #doMinicourseFullItemSelect(lessonId) {
    if (UserLessons.isComplete(lessonId)) {
      this.#transitionUtil.minicourseToRecap(lessonId);
      return;
    }
    const isSame = (Global.lessonId === lessonId);
    if (isSame) {
      this.#transitionUtil.minicourseToPreexistingLesson();
      return;
    }
    this.#transitionUtil.minicourseToLesson(lessonId);
  }

  /** User has selected an item from the minicourse sidebar. */
  #doMinicourseSidebarItemSelect(lessonId) {
    this.minicourseView.closeSidebar();
    this.#areGlobalElementsEnabled = true;

    const isSameLesson = (Global.lessonId === lessonId);
    if (isSameLesson) {
      this.lessonView.reshow();
      return;
    }

    this.#transitionUtil.lessonDoneOrRecapToDifferentLessonOrRecap(lessonId);
  }

  #openMinicourseSidebar() {
    this.minicourseView.openSidebar();
    this.#areGlobalElementsEnabled = false;
    this.#updateCloseButton();
    switch (Global.currentViewType) {
      case "lesson":
        this.lessonView.isEnabled = false;
        break;
      case "done":
        this.doneView.isEnabled = false;
        break;
      case "recap":
        this.recapView.isEnabled = false;
        break;
    }
  }

  #closeMinicourseSidebar() {
    this.minicourseView.closeSidebar();
    this.#areGlobalElementsEnabled = true;
    this.#updateCloseButton();
    switch (Global.currentViewType) {
      case "lesson":
        this.lessonView.isEnabled = true;
        break;
      case "done":
        this.doneView.isEnabled = true;
        break;
      case "recap":
        this.recapView.isEnabled = true;
        break;
    }
  }

  #openLessonGallery(thumb) {
    if (Global.minicourseData) {
      this.minicourseView.hide();
    }
    this.#areGlobalElementsEnabled = false;
    this.lessonView.showGalleryView(thumb);
    this.#updateCloseButton();
  }

  #closeLessonGallery() {
    if (Global.minicourseData) {
      this.minicourseView.showAsClosedSidebar();
    }
    this.#areGlobalElementsEnabled = true;
    this.lessonView.closeGalleryView();
    this.#updateCloseButton();
  }

  #updateCloseButton() {
    let shouldShow = false;
    if ((this.lessonView && this.lessonView.isGallery) || Global.cmsDevJson) {
      shouldShow = true;
    } else if (Global.displayMode === "overlay") {
      // Always show in overlay mode unless sidebar is open and window is too narrow
      if (!this.minicourseView) {
        shouldShow = true;
      } else if ($(window).width() > 480) {
        shouldShow = true;
      } else {
        shouldShow = !this.minicourseView.isSidebar || this.minicourseView.isClosed;
      }
    }
    if (shouldShow) {
      FadeUtil.fadeIn(this.#$closeButton);
    } else {
      FadeUtil.fadeOut(this.#$closeButton);
    }
  }

  set #areGlobalElementsEnabled(b) {
    // In practice, this means just footer stuff, for now.
    // Also, close button always stays enabled.
    Util.setAriaHidden(this.#$footer, !b);
    if (b) {
      this.#$footerPrivacyLink.attr("tabindex", Constants.TABINDEX_FOOTER_PRIVACY);
      this.#$footerBackButton.attr("tabindex", Constants.TABINDEX_FOOTER_BACK);
      this.#$footerLogo.find("a").attr("tabindex", Constants.TABINDEX_FOOTER_LOGO);
    } else {
      this.#$footerPrivacyLink.attr("tabindex", -1);
      this.#$footerBackButton.attr("tabindex", -1);
      this.#$footerLogo.find("a").attr("tabindex", -1);
    }
  }

  // -----------------------------------------------------------------------------------------------
  // Event handlers

  /**
   * Gets called when GAPI has been initialized in the containing page.
   */
  #onGapiInit(e, isAlreadySignedIn) {
    cl('onGapiInit - isalready?', isAlreadySignedIn)
    if (isAlreadySignedIn) {
      this.#signInStatus = "sign_in_flow";
      ServiceUtil.doSignInFlow(true, o => this.#onSignInResult(o));
    } else {
      this.#signInStatus = "signed_out";
    }
    this.#updateLiveModeViews();
  }

  #onSignInButton(e) {
    this.#signInStatus = "sign_in_flow";
    ServiceUtil.doSignInFlow(false, o => this.#onSignInResult(o));
  }

  /**
   * @param result true if success
   */
  #onSignInResult(result) {
    if (!result) {
      this.#signInStatus = "signed_out";
      this.#updateLiveModeViews();
      return;
    }

    this.#signInStatus = "okay";
    this.#accountView.populate();
    this.#updateLiveModeViews();

    this.#animateIn();
  }

  /**
   * Should be the only window.resize handler in the app.
   */
  #onWindowResize(e) {
    // Detect if breakpoint was triggered.
    this.#wasMobileWidth = this.#isMobileWidth;
    this.#isMobileWidth = Metrics.isMobileWidth;
    this.#didLayoutChange = (this.#isMobileWidth !== this.#wasMobileWidth);
    if (this.#didLayoutChange) {
      Analytics.trackBreakpointChange(this.#isMobileWidth);
    }
    this.#resize();
  }

  #onMinicourseItemSelect(e, lessonId) {
    if (this.minicourseView.isSidebar) {
      this.#doMinicourseSidebarItemSelect(lessonId)
    } else {
      this.#doMinicourseFullItemSelect(lessonId)
    }
  }

  /** Rem, this handles next lesson button from either done view or replay view. */
  #onNextLessonButton(e, lessonId) {
    this.#transitionUtil.lessonDoneOrRecapToDifferentLessonOrRecap(lessonId,
        true);
  }

  #onLessonDone(e) {
    // TODO: This isn't correct anymore. Use css rules.
    // if (Global.hasCta && !Global.minicourseData) FadeUtil.fadeOut(this.#$cta);

    this.#transitionUtil.lessonToDone();
  }

  #onDoneViewRecapButton(e) {
    this.#transitionUtil.doneToRecap();
  }

  #onRecapViewReplay(e) {
    Analytics.lessonTrackingSource = "recap";
    this.#transitionUtil.recapToReplay();
  }

  // Rem, this only gets triggered on the first "click" on a "DragDelegate".
  #onTouchDeviceDetermined(e) {
    if (DragDelegate.isTouchDevice) {
      $('body').addClass('isTouchDevice');
    }
  }

  #onSignOutButton(e) {
    // TODO: while waiting, block view while waiting and show spinner
    gapi.auth2.getAuthInstance().signOut().then(() => {
      this.#signInStatus = "signed_out";
      this.#updateLiveModeViews();
    }).catch(err => {
      cl(err);
    });
  }

  #onGalleryThumbClick(e, thumb) {
    this.#openLessonGallery(thumb);
  }

  #onMainCtaClick(e) {
    Analytics.trackCustomCTA(Global.ctaLink);
  }

  #onFooterBackButtonClick(e) {
    Analytics.trackBackToDashboardButton();
    this.#transitionUtil.anyToMinicourse();
  }

  #onDoneViewBackToDashboard(e) {
    this.#transitionUtil.anyToMinicourse();
  }

  #onFooterLogoClick(e) {
    if (Global.currentViewType === "lesson") {
      Analytics.trackPrimerButton("desktop_footer_lesson");
    } else {
      Analytics.trackPrimerButton("desktop_footer_list");
    }
  }

  #onMobileDownloadCtaClick(e) {
    if (this.minicourseView.isSidebar) {
      Analytics.trackPrimerButton("mobile_drawer");
    } else {
      Analytics.trackPrimerButton("mobile_list");
    }
  }

  #onRecapMobileDownloadCtaClick(e) {
    Analytics.trackPrimerButton("mobile_recap");
  }

  #onDocumentKeyDown(e) {
    switch (e.key) {
      case "Esc":
      case "Escape":
        if (this.minicourseView && this.minicourseView.isSidebar
            && !this.minicourseView.isClosed) {
          this.#closeMinicourseSidebar();
        } else if (this.lessonView.isGallery) {
          this.#closeLessonGallery();
        }
        break;
      case "e":
        if (!Global.isProd) {
          // Non-production only! Set all-but-one lessons to complete
          window.location.href = window.location.href + "&complete=yes";
        }
        break;
      case "r":
        // Non-production only! Erase user progress.
        if (!Global.isProd) {
          window.location.href = window.location.href + "&erase=yes";
        }
        break;
    }
  }

  #onCloseButton(e) {
    if (this.lessonView && this.lessonView.isGallery) {
      this.#closeLessonGallery();
    } else {
      // Tell parent window to close iframe (this).
      AppUtil.sendCloseToParentWindow()
    }
  }

  /**
   * Handler does nothing, just has to exist in order to defeat Mobile Safari double-tap-to-zoom
   * behavior in some instances. Watch out for any unintended side-effects.
   */
  #onBodyClick(e) { }
}
